import React from 'react';
import {
  Icon,
  chakra,
  Flex,
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@chakra-ui/react';
import { ChevronDown } from 'styled-icons/boxicons-solid';
import english from '@/assets/core/images/language/english.png';
import mandarin from '@/assets/core/images/language/mandarin.png';
import { locale, setLocale } from '@/helpers/utils';

export const LangButton = chakra(Flex, {
  baseStyle: {
    display: 'flex',
    height: '46px',
    width: '82px',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    borderRadius: '6px',
    bgGradient: 'linear(to-b, #BA0700, #640502)',
    border: '2px solid #FFC033',
    cursor: 'pointer',
    position: 'relative',
    _hover: { opacity: 0.9 },
  },
});

export const InnerBorder = chakra(Box, {
  baseStyle: {
    position: 'absolute',
    inset: '0px',
    borderRadius: '6px',
    border: '1px solid #640502',
    pointerEvents: 'none',
  },
});

export const LangText = chakra('span', {
  baseStyle: {
    zIndex: 1,
    color: '#FFFFBF',
    fontFamily: 'Staatliches',
    fontWeight: '400',
    fontSize: '14px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
});

export const FlagBox = chakra(Box, {
  baseStyle: {
    w: '30px',
    h: '30px',
    overflow: 'hidden',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    zIndex: 1,
  },
});

export const LangPopoverContent = chakra(PopoverContent, {
  baseStyle: {
    border: 'none',
    bg: 'transparent',
    boxShadow: 'none',
    w: '180px',
    p: 0,
    m: 0,
    _focus: { boxShadow: 'none' },
  },
});

export const LangDropdown = chakra(Box, {
  baseStyle: {
    bgGradient: 'linear(to-b, #BA0700, #640502)',
    border: '3px solid #FFC033',
    borderRadius: '6px',
  },
});

export const LangDropdownHeader = chakra(Flex, {
  baseStyle: {
    align: 'center',
    justifyContent: 'space-between',
    px: '10px',
    py: '10px',
    borderRadius: '4px',
    borderBottomColor: '#751410',
    borderBottomWidth: '2px',
    borderBottomRadius: '0px',
  },
});

export const LangDropdownTitle = chakra(Box, {
  baseStyle: {
    color: '#FFFFBF',
    fontFamily: 'Staatliches',
    fontSize: '14px',
    letterSpacing: '0.5px',
  },
});

export const LangList = chakra(Box, {
  baseStyle: {},
});

export const LangDivider = chakra(Box, {
  baseStyle: {
    borderBottom: '1px dashed #B13C2C',
    mx: '4px',
  },
});

export const LangItem = chakra(Flex, {
  baseStyle: {
    alignItems: 'center',
    gap: '8px',
    px: '8px',
    py: '10px',
  },
});

export const LangItemLabel = chakra(Box, {
  baseStyle: {
    fontFamily: 'Staatliches',
    fontSize: '18px',
    letterSpacing: '0.5px',
  },
});

// ============ Data & Component chính ============

const FLAG_MAP: Record<string, string> = {
  en: typeof english === 'string' ? english : (english as any).src,
  zh: typeof mandarin === 'string' ? mandarin : (mandarin as any).src,
};

const LANG_CONFIG = [
  { code: 'en', label: 'ENGLISH', img: FLAG_MAP.en },
  { code: 'zh', label: 'MANDARIN', img: FLAG_MAP.zh },
];

export default function LanguageButton() {
  const currentFlag = FLAG_MAP[locale] ?? FLAG_MAP.en;

  return (
    <Popover placement="bottom-end" gutter={0} offset={[0, -46]}>
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <LangButton as="button">
              <InnerBorder />

              <FlagBox bgImage={`url(${currentFlag})`} />

              <LangText>
                {locale === 'en' && 'EN'}
                {locale === 'zh' && 'ZH'}
              </LangText>

              <Icon
                as={ChevronDown}
                fontSize="16px"
                zIndex={1}
                color="#FFFFBF"
              />
            </LangButton>
          </PopoverTrigger>

          <LangPopoverContent>
            <InnerBorder />

            <LangDropdown>
              <LangDropdownHeader onClick={onClose}>
                <LangDropdownTitle>SELECT LANGUAGE</LangDropdownTitle>
                <Icon as={ChevronDown} fontSize="16px" color="#FFFFBF" />
              </LangDropdownHeader>

              <LangList>
                {LANG_CONFIG.map((item, index) => {
                  const isActive = locale === item.code;

                  return (
                    <React.Fragment key={item.code}>
                      {index === 1 && <LangDivider />}

                      <LangItem
                        cursor={isActive ? 'default' : 'pointer'}
                        opacity={isActive ? 0.35 : 1}
                        _hover={
                          isActive
                            ? { bgGradient: 'none' }
                            : {
                                bgGradient:
                                  'linear(to-b, #BA0700, rgba(230, 0, 18, 1))',
                              }
                        }
                        onClick={() => {
                          if (!isActive) {
                            setLocale(item.code);
                          }
                        }}
                      >
                        <FlagBox bgImage={`url(${item.img})`} />

                        <LangItemLabel color={isActive ? '#C08D7A' : '#FFFFBF'}>
                          {item.label}
                        </LangItemLabel>
                      </LangItem>
                    </React.Fragment>
                  );
                })}
              </LangList>
            </LangDropdown>
          </LangPopoverContent>
        </>
      )}
    </Popover>
  );
}
